import React from 'react';
import PropTypes from 'prop-types';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import {
  getCardImageField,
  resolveScaledImageUrl,
  createImageErrorHandler,
  cardImageFit,
} from '../../../helpers/resolveCardImage';
import Image from '@plone/volto/components/theme/Image/Image';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

/**
 * The image half of a listing card: the `.cards__image-container`, the
 * image itself, the optional link around it, the copyright badge and any
 * overlay (the Video card's play badge).
 *
 * Every Flex*Card used to inline this, which is how the three concerns
 * below ended up applied to only some of them:
 * - `createImageErrorHandler`, so a broken image URL falls back to the
 *   type's default picture instead of the browser's broken-image icon,
 * - `loading="lazy"` / `decoding="async"`, which matters on listings that
 *   show 25 images at once,
 * - the copyright badge, whose absence silently dropped the attribution
 *   of an image depending on which listing it happened to appear in.
 *
 * Callers pass their type's `fallback` picture and, where the layout
 * differs from the default (People's portrait column, News' top image),
 * an explicit `containerClass`.
 */
const CardImage = ({
  item,
  fallback,
  field = 'image',
  scalePreference = ['preview', 'mini'],
  containerClass,
  gridLayout = false,
  href,
  alt,
  width = 335,
  height = 188,
  overlay = null,
  speculative = false,
}) => {
  const imageSource = getCardImageField(item, field);
  const imageUrl =
    resolveScaledImageUrl(item, imageSource, scalePreference) ||
    (speculative && !imageSource
      ? // No field found at all (e.g. Lead Image just set, catalog hasn't
        // reindexed yet) - a speculative @@images URL still beats showing
        // the generic default, and the error handler catches it if wrong.
        `${flattenToAppURL(item['@id'])}/@@images/${field}/preview`
      : fallback);

  const layoutClass =
    containerClass ??
    (gridLayout
      ? 'cards__image-container--top cards__image-container--top-grid'
      : 'cards__image-container--left');

  // Whether the picture may be cropped to the card's 16:9 box at all, and
  // where the crop sits if it may; see cardImageFit. The backend fills both
  // in when the image is saved, so a new photo needs no editing.
  const imageFit = cardImageFit(item);

  const imageEl = (
    <Image
      className="cards__image"
      src={imageUrl}
      alt={alt ?? item.image_caption ?? item.title ?? ''}
      width={width}
      height={height}
      loading="lazy"
      decoding="async"
      style={imageFit ?? undefined}
      onError={createImageErrorHandler(fallback)}
    />
  );

  return (
    <div className={`cards__image-container ${layoutClass}`}>
      {href ? <UniversalLink href={href}>{imageEl}</UniversalLink> : imageEl}
      {overlay}
      {item.image_copyright && (
        <span
          className={`cards__copyright-badge${
            item.image_copyright_black_font
              ? ' cards__copyright-badge--black'
              : ''
          }`}
        >
          © {item.image_copyright}
        </span>
      )}
    </div>
  );
};

CardImage.propTypes = {
  item: PropTypes.shape({
    '@id': PropTypes.string.isRequired,
    title: PropTypes.string,
    image_caption: PropTypes.string,
    image_copyright: PropTypes.string,
    image_copyright_black_font: PropTypes.bool,
    image_scales: PropTypes.object,
    /** Optional manual override for the computed crop position, e.g. '30%'. */
    image_focal_point: PropTypes.string,
    /** 'contain' shows the whole picture instead of cropping it. */
    image_fit: PropTypes.string,
  }).isRequired,
  /** Picture shown when the item has no image, or its URL fails to load. */
  fallback: PropTypes.string.isRequired,
  /** Image field to read; 'logo' for Sponsor, which has no Lead Image. */
  field: PropTypes.string,
  scalePreference: PropTypes.arrayOf(PropTypes.string),
  /** Overrides the gridLayout-derived container modifier. */
  containerClass: PropTypes.string,
  gridLayout: PropTypes.bool,
  /** Omit to render the image without a link. */
  href: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  overlay: PropTypes.node,
  /**
   * Try `<item>/@@images/<field>/preview` when the item carries no image
   * field at all, instead of going straight to `fallback`. Costs a failed
   * request per image-less item, so it stays opt-in for the types whose
   * cards already behaved that way.
   */
  speculative: PropTypes.bool,
};

export default CardImage;
